import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useMercenary } from '../../../contexts/MercenaryContext';
import Toast from '../../Shared/Toast';
import { loadCookiePrefs } from './lobbyPreferences';
import styles from './CreateLobby.module.css';

const getLobbyPrefsKey = (mercId: number) => `team_battle_lobby_prefs_${mercId}`;

interface LobbyPrefs {
  minLevelOffset: number;
  maxLevelOffset: number;
  maxPlayers: number;
  randomTeams: boolean;
  matchType: 'normal' | 'death';
  pve: boolean;
  allowBotFill: boolean;
  name?: string;
}

const CreateLobby: React.FC = () => {
  const { mercenary } = useMercenary();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [minLevel, setMinLevel] = useState(1);
  const [maxLevel, setMaxLevel] = useState(5);
  const [maxPlayers, setMaxPlayers] = useState(4);
  const [randomTeams, setRandomTeams] = useState(false);
  const [matchType, setMatchType] = useState<'normal' | 'death'>('normal');
  const [pve, setPve] = useState(false);
  const [allowBotFill, setAllowBotFill] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastType, setToastType] = useState<'success' | 'error'>('success');

  const applyDefaults = (merc: NonNullable<typeof mercenary>) => {
    setName((merc.name.replace(/[^a-zA-Z0-9 ]/g, '').trim() + ' Team Battle').slice(0, 30));
    setMinLevel(Math.max(1, merc.level - 2));
    setMaxLevel(merc.level + 4);
  };

  useEffect(() => {
    if (!mercenary) return;
    const saved = localStorage.getItem(getLobbyPrefsKey(mercenary.id));
    if (saved) {
      try {
        const prefs: LobbyPrefs = JSON.parse(saved);
        setName((prefs.name ? prefs.name.replace(/[^a-zA-Z0-9 ]/g, '').trim() : '') || (mercenary.name.replace(/[^a-zA-Z0-9 ]/g, '').trim() + ' Team Battle').slice(0, 30));
        setMinLevel(Math.max(Math.max(1, mercenary.level - 2), Math.min(mercenary.level, mercenary.level + prefs.minLevelOffset)));
        setMaxLevel(Math.max(mercenary.level, Math.min(mercenary.level + 4, mercenary.level + prefs.maxLevelOffset)));
        setMaxPlayers(prefs.maxPlayers);
        setRandomTeams(prefs.randomTeams);
        setMatchType(prefs.matchType);
        setPve(prefs.pve);
        setAllowBotFill(prefs.allowBotFill ?? false);
      } catch {
        applyDefaults(mercenary);
      }
    } else {
      applyDefaults(mercenary);
    }
  }, [mercenary]);

  const handlePveChange = (val: boolean) => {
    setPve(val);
    if (val) {
      setRandomTeams(false);
      setMatchType('normal');
    } else {
      setAllowBotFill(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mercenary?.id) return;
    setIsLoading(true);
    const lobbyName = name.trim() || (mercenary.name.replace(/[^a-zA-Z0-9 ]/g, '').trim() + ' Team Battle').slice(0, 30);
    try {
      const battleType = pve ? 'pve' : matchType;
      const cookiePrefs = loadCookiePrefs(mercenary.id, battleType);
      const savedPreferences = {
        surrenderHP: cookiePrefs?.surrenderHP ?? 0.5,
        readyHP: cookiePrefs?.readyHP ?? 0.9,
        strategy: cookiePrefs?.strategy ?? 'Normal',
      };
      const response = await axios.post(
        '/api/team-battle/create',
        { name: lobbyName, minLevel, maxLevel, maxPlayers, randomTeams, matchType, pve, allowBotFill, mercId: mercenary.id },
        { withCredentials: true }
      );
      const lobbyId = response.data.lobby.id;
      await axios.put(
        `/api/team-battle/lobbies/${lobbyId}/participant/${mercenary.id}`,
        savedPreferences,
        { withCredentials: true }
      );
      localStorage.setItem(getLobbyPrefsKey(mercenary.id), JSON.stringify({
        minLevelOffset: minLevel - mercenary.level,
        maxLevelOffset: maxLevel - mercenary.level,
        maxPlayers,
        randomTeams,
        matchType,
        pve,
        allowBotFill,
        name: lobbyName,
      } as LobbyPrefs));
      navigate(`/team-battle/lobby/${lobbyId}`);
    } catch (err: any) {
      setToastType('error');
      setToastMessage(err?.response?.data?.message ?? 'Failed to create lobby.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!mercenary) return null;

  const minLevelMin = Math.max(1, mercenary.level - 2);
  const minLevelMax = mercenary.level;
  const maxLevelMin = mercenary.level;
  const maxLevelMax = mercenary.level + 4;

  return (
    <div className={styles.createLobbyContainer}>
      {toastMessage && (
        <Toast message={toastMessage} type={toastType} duration={3000} onClose={() => setToastMessage(null)} />
      )}
      <h1 className={styles.title}>⚔ Create Lobby</h1>
      <div className={styles.titleDecor}>- Muster a War Party -</div>
      <hr className={styles.divider} />

      <form className={styles.form} onSubmit={handleCreate}>

        <div className={styles.formGroup}>
          <label>Lobby Name</label>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value.replace(/[^a-zA-Z0-9 ]/g, '').slice(0, 30))}
            placeholder={(mercenary.name.replace(/[^a-zA-Z0-9 ]/g, '').trim() + ' Team Battle').slice(0, 30)}
            maxLength={30}
          />
        </div>

        <div className={styles.formGroup}>
          <label>Min Level</label>
          <div className={styles.numberInputWrapper}>
            <button type="button" className={styles.numberBtn}
              onClick={() => setMinLevel(l => Math.max(minLevelMin, l - 1))}>−</button>
            <input type="number" value={minLevel} readOnly className={styles.numberControl} />
            <button type="button" className={styles.numberBtn}
              onClick={() => setMinLevel(l => Math.min(maxLevel, l + 1))}>+</button>
          </div>
        </div>

        <div className={styles.formGroup}>
          <label>Max Level</label>
          <div className={styles.numberInputWrapper}>
            <button type="button" className={styles.numberBtn}
              onClick={() => setMaxLevel(l => Math.max(minLevel, l - 1))}>−</button>
            <input type="number" value={maxLevel} readOnly className={styles.numberControl} />
            <button type="button" className={styles.numberBtn}
              onClick={() => setMaxLevel(l => Math.min(maxLevelMax, l + 1))}>+</button>
          </div>
        </div>

        <div className={styles.formGroup}>
          <label>Team Size</label>
          <select value={maxPlayers} onChange={e => setMaxPlayers(+e.target.value)}>
            <option value={4}>2 vs. 2</option>
            <option value={6}>3 vs. 3</option>
            <option value={8}>4 vs. 4</option>
          </select>
        </div>

        <div className={`${styles.toggleRow} ${randomTeams ? styles.toggleRowDisabled : ''}`}>
          <div className={styles.toggleLabel}>
            <span className={styles.toggleLabelMain}>PvE Mode</span>
            <span className={styles.toggleLabelSub}>Team vs beasts, no opposing players</span>
          </div>
          <label className={styles.toggle}>
            <input type="checkbox" checked={pve} disabled={randomTeams} onChange={e => handlePveChange(e.target.checked)} />
            <span className={styles.toggleSlider}></span>
          </label>
        </div>

        <div className={`${styles.toggleRow} ${pve ? styles.toggleRowDisabled : ''}`}>
          <div className={styles.toggleLabel}>
            <span className={styles.toggleLabelMain}>Random Teams</span>
            <span className={styles.toggleLabelSub}>Auto-assign mercenaries when lobby fills</span>
          </div>
          <label className={styles.toggle}>
            <input type="checkbox" checked={randomTeams} disabled={pve} onChange={e => setRandomTeams(e.target.checked)} />
            <span className={styles.toggleSlider}></span>
          </label>
        </div>

        <div className={`${styles.toggleRow} ${pve ? styles.toggleRowDisabled : ''}`}>
          <div className={styles.toggleLabel}>
            <span className={styles.toggleLabelMain}>Death Match</span>
            <span className={styles.toggleLabelSub}>No surrender - last side standing wins</span>
          </div>
          <label className={styles.toggle}>
            <input type="checkbox" checked={matchType === 'death'} disabled={pve} onChange={e => setMatchType(e.target.checked ? 'death' : 'normal')} />
            <span className={styles.toggleSlider}></span>
          </label>
        </div>

        <div className={`${styles.toggleRow} ${!pve ? styles.toggleRowDisabled : ''}`}>
          <div className={styles.toggleLabel}>
            <span className={styles.toggleLabelMain}>Bot Fill</span>
            <span className={styles.toggleLabelSub}>PvE only, bots fill empty slots if low activity</span>
          </div>
          <label className={styles.toggle}>
            <input type="checkbox" checked={allowBotFill} disabled={!pve} onChange={e => setAllowBotFill(e.target.checked)} />
            <span className={styles.toggleSlider}></span>
          </label>
        </div>

        <div className={styles.formGroup}>
          <button type="submit" className={styles.createLobbyButton} disabled={isLoading}>
            {isLoading ? 'Creating…' : '⚔ Create Lobby'}
          </button>
          <button type="button" className={styles.backButton} onClick={() => navigate('/team-battle')}>
            ← Back to Lobbies
          </button>
        </div>

      </form>
    </div>
  );
};

export default CreateLobby;
